import {
  IconPlaneTilt,
  IconWorldSearch,
  IconPlayerPause,
  IconBuildingStore,
  IconShoppingBag,
  IconCash,
  IconBell,
  IconCertificate,
  IconIdBadge2,
  IconMail,
  IconMessage,
  IconTicket,
  IconUserEdit
} from '@tabler/icons-react';

const typeConfig = {
  flight_deal_match: { icon: IconPlaneTilt, label: 'Flight Deal' },
  ai_flight_deal_ready: { icon: IconWorldSearch, label: 'AI Flight Deal' },
  flight_deal_tracker_disabled: { icon: IconPlayerPause, label: 'Tracker Disabled' },
  trip_guide_sold: { icon: IconBuildingStore, label: 'Trip Guide Sold' },
  trip_guide_purchased: { icon: IconShoppingBag, label: 'Trip Guide Purchased' },
  payment_received: { icon: IconCash, label: 'Payment Received' },
  host_application: { icon: IconIdBadge2, label: 'Host Application' },
  host_profile_change: { icon: IconUserEdit, label: 'Profile Change' },
  host_credential: { icon: IconCertificate, label: 'Credential' },
  host_enquiry: { icon: IconMail, label: 'Enquiry' },
  host_message: { icon: IconMessage, label: 'Message' },
  guest_message: { icon: IconMessage, label: 'Message' },
  host_booking: { icon: IconTicket, label: 'Booking' },
  guest_booking: { icon: IconTicket, label: 'Booking' }
};

const priorityColorMap = {
  success: '#00c853',
  urgent: '#f44336',
  info: '#757575'
};

export const getNotificationConfig = (type, priority) => {
  const config = typeConfig[type] || { icon: IconBell, label: 'Notification' };
  const color = priorityColorMap[priority] || priorityColorMap.info;
  return { ...config, color };
};

// Types that open a dialog instead of navigating
const dialogTypes = ['ai_flight_deal_ready'];

export const isDialogNotification = (type) => dialogTypes.includes(type);

// Per-type overrides (when the same resource_type needs different routes)
const typeRouteOverrides = {
  trip_guide_sold: () => '/influencer/sales-dashboard',
  // The travel host notifications all carry resource_type 'host_profile', so
  // they route by type. An application verdict goes to the wizard root, whose
  // resume rule sends an approved host to /host/profile and a rejected one to
  // the checklist with the reason pinned (host-profile-tech-design §9.6).
  host_application: () => '/host/onboarding',
  host_profile_change: () => '/host/profile#change',
  host_credential: () => '/host/profile#credentials',
  // A new enquiry opens the enquiry itself, not the list (host-inbox-tech-design
  // D5); opening it marks it read, which clears the inbox badge too. Without an
  // id there is nothing to open, so the inbox is the honest fallback. `src=bell`
  // feeds the inbox's `entry` analytics, as `src=email` does from the email
  // link; the page reads it once and drops it from the URL.
  host_enquiry: (id) => (id ? `/host/inbox/${id}?src=bell` : '/host/inbox?src=bell'),
  // A message in a conversation (host-messaging-tech-design §8.1): one bell
  // item per thread, refreshed rather than repeated. The host's opens the
  // thread in the inbox; the guest's opens it in Messages.
  host_message: (id) => (id ? `/host/inbox/${id}?src=bell` : '/host/inbox?src=bell'),
  guest_message: (id) => (id ? `/messages/${id}?src=bell` : '/messages?src=bell'),
  // A booking request or its change (host-availability-tech-design §7.2, BK-11):
  // one bell item per side per thread, carrying resource_type 'host_enquiry'
  // and the thread id, so it opens the thread where the booking is pinned and
  // reading the thread clears it. Without these, both types would fall through
  // to resourceRouteMap, which has no route for a thread.
  host_booking: (id) => (id ? `/host/inbox/${id}?src=bell` : '/host/inbox?src=bell'),
  guest_booking: (id) => (id ? `/messages/${id}?src=bell` : '/messages?src=bell')
};

// Route map for navigation-based notifications only
const resourceRouteMap = {
  flight_deal: () => '/collection',
  user_flight_deal_tracker: (id) => `/notification-settings?tracker_id=${id}`,
  trip_guide: (id) => `/trip-guide-studio?ptg_id=${id}`
  // stripe_transfer -- deferred
};

export const getNotificationUrl = (resourceType, resourceId, notificationType) => {
  const overrideFn = notificationType && typeRouteOverrides[notificationType];
  if (overrideFn) return overrideFn(resourceId);
  const routeFn = resourceRouteMap[resourceType];
  return routeFn ? routeFn(resourceId) : null;
};

// A trailing `Z` or `±hh:mm` (or `±hhmm`) offset.
const ZONE_DESIGNATOR = /(?:Z|[+-]\d{2}:?\d{2})$/i;

// A date with a time of day, e.g. `2026-10-07T14:36:12.123456` or `2026-10-07 14:36:12`.
const DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/**
 * A notification timestamp as a Date. The notifications API sends naive ISO
 * strings: UTC on the server, but with no zone designator, which `new Date`
 * reads as the viewer's local time. That put a host at UTC+8 eight hours
 * behind a notification that had just arrived. A naive date-time is read as
 * UTC; one that already carries a zone keeps it. Anything that is not a
 * date-time string goes to `new Date` untouched.
 */
export const parseNotificationDate = (value) => {
  if (typeof value !== 'string' || !DATE_TIME.test(value.trim())) return new Date(value);
  // Safari will not parse a space between the date and the time.
  const iso = value.trim().replace(' ', 'T');
  return new Date(ZONE_DESIGNATOR.test(iso) ? iso : `${iso}Z`);
};

export const formatRelativeTime = (dateString) => {
  const now = new Date();
  const date = parseNotificationDate(dateString);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};
